if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.autoplayVisibleVideos();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      autoplayVisibleVideos() {
        // Swiping or reloading onto a video slide never calls setActiveMedia, so watch visibility instead
        const videoSlides = [...this.elements.viewer.querySelectorAll('.product__media-item')].filter((slide) =>
          slide.querySelector('deferred-media')
        );
        if (!videoSlides.length) return;

        // iOS draws its own play button over a blocked <video autoplay> or one with controls (Low Power Mode
        // blocks even muted autoplay), so the videos carry neither and are started from script instead
        videoSlides.forEach((slide) =>
          slide.querySelector('deferred-media template')?.content.querySelector('video')?.removeAttribute('autoplay')
        );

        // Buffering a video costs every visitor ~3 MB, and before the slider CSS applies the slides stack
        // vertically, so a later video slide briefly looks visible on load. Only buffer once the shopper
        // touches the gallery (swipe, arrow, dot or key), unless the video is the first slide.
        const firstSlide = this.elements.viewer.querySelector('.product__media-item');
        const peeking = new Set();
        const nearbyVideoSlides = () => {
          const slides = [...this.elements.viewer.querySelectorAll('.product__media-item')];
          const list = slides[0].parentElement;
          const current = slides.findIndex((slide) => Math.abs(slide.offsetLeft - list.scrollLeft) < slide.offsetWidth / 2);
          return videoSlides.filter((slide) => peeking.has(slide) || Math.abs(slides.indexOf(slide) - current) <= 1);
        };
        // Runs on every touch: WebKit treats touchstart as a user gesture for media, so the muted play/pause here
        // unlocks the next video for the script play() that follows the swipe, even in Low Power Mode
        const startBuffering = (event) => {
          this.galleryTouched = true;
          // A tap on an arrow or dot may jump anywhere, so prepare every video slide
          const slides = event.target.closest?.('.slider-button, .product-gallery-dots') ? videoSlides : nearbyVideoSlides();
          slides.forEach((slide) => this.primeVideo(slide.querySelector('deferred-media')));
        };
        this.addEventListener('touchstart', startBuffering, { passive: true });
        this.addEventListener('pointerdown', startBuffering, { passive: true });
        this.addEventListener('keydown', startBuffering);

        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const deferredMedia = entry.target.querySelector('deferred-media');
              if (entry.intersectionRatio > 0) {
                peeking.add(entry.target);
              } else {
                peeking.delete(entry.target);
              }
              // Buffer while the slide only peeks, so playback starts instantly once it is swiped in
              if (entry.intersectionRatio > 0 && (this.galleryTouched || entry.target === firstSlide)) {
                this.preloadVideo(deferredMedia);
              }
              // Play only once the shopper is swiping the slide in, not while it just peeks. Kept on the
              // wrapper so a video created later by a touch knows whether its slide is already on screen.
              deferredMedia.dataset.wantPlay = entry.intersectionRatio >= 0.3 ? 'true' : '';
              const video = deferredMedia.querySelector('video');
              if (!video) return;

              if (!deferredMedia.dataset.wantPlay) {
                // A warm-up still in flight pauses itself; pausing now would abort the play() the touch unlocked
                if (!video.dataset.warming) video.pause();
              } else if (video.paused) {
                // If the browser still refuses, the first frame stays on screen and a tap starts it
                video.play().catch(() => {});
              }
            });
          },
          { threshold: [0, 0.01, 0.3] }
        );
        videoSlides.forEach((slide) => observer.observe(slide));

        // No controls ever: a tap on the video (or on the poster before it loads) just plays it
        this.elements.viewer.addEventListener('click', (event) => {
          const video = event.target.closest('deferred-media')?.querySelector('video');
          if (video?.paused) video.play().catch(() => {});
        });
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      preloadVideo(deferredMedia) {
        if (!deferredMedia || deferredMedia.getAttribute('loaded')) return;
        const source = deferredMedia.querySelector('template')?.content.querySelector('video');
        if (!source) return;

        // loadContent() would pause other media and start playback, so create the video quietly
        source.removeAttribute('autoplay');
        source.setAttribute('preload', 'auto');
        const pauseAllMedia = window.pauseAllMedia;
        window.pauseAllMedia = () => {};
        try {
          deferredMedia.loadContent(false);
        } finally {
          window.pauseAllMedia = pauseAllMedia;
        }

        const video = deferredMedia.querySelector('video');
        if (video) this.warmVideo(video);
      }

      // Called from a touch: creates the video if needed, then retries the warm-up until the browser accepts one
      primeVideo(deferredMedia) {
        this.preloadVideo(deferredMedia);
        const video = deferredMedia?.querySelector('video');
        if (video && !video.dataset.primed && video.paused) this.warmVideo(video);
      }

      // iOS may skip preload on a paused video; a muted play/pause warms the decoder and shows the first frame
      warmVideo(video) {
        video.dataset.warming = 'true';
        video
          .play()
          .then(() => {
            video.dataset.primed = 'true';
            if (video.closest('deferred-media').dataset.wantPlay) return;
            video.pause();
            video.currentTime = 0;
          })
          .catch(() => {})
          .finally(() => delete video.dataset.warming);
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem?.querySelector('.deferred-media');
        if (!deferredMedia) return;
        if (!deferredMedia.getAttribute('loaded')) deferredMedia.loadContent(false);
        deferredMedia.querySelector('video')?.play().catch(() => {});
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
